import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'
import { getInitialAuthUrlState, isDeadLinkError } from '../../services/authUrlState'
import { validatePassword } from '../../services/passwordPolicy'

// Age in full years from a YYYY-MM-DD string; null if unparseable.
function ageFromDob(dobStr) {
  if (!dobStr) return null
  const d = new Date(dobStr)
  if (isNaN(d.getTime())) return null
  const now = new Date()
  let age = now.getFullYear() - d.getFullYear()
  const m = now.getMonth() - d.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--
  return age
}

export function AuthView() {
  const { signIn, signUp, loading, requestPasswordReset, resendConfirmation } = useAuth()
  const [mode, setMode] = useState('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [terms, setTerms] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [error, setError] = useState(null)
  const [dob, setDob] = useState('')
  const [ageConfirm, setAgeConfirm] = useState(false)
  const [blocked, setBlocked] = useState(false)
  const [checkEmail, setCheckEmail] = useState(false)
  const [busy, setBusy] = useState(false)
  // A dead email link (expired, already used, or opened by a mail scanner
  // before the person got to it) used to be discarded in silence, leaving them
  // on a blank sign-in card wondering what they had done wrong.
  const [linkError, setLinkError] = useState(() => {
    const s = getInitialAuthUrlState()
    return s.kind === 'error' ? s : null
  })
  const [notice, setNotice] = useState(null)
  // Closed-beta capacity (international beta wave, Sep 2026). The authoritative
  // gate is the Supabase Before User Created hook; this pre-check only decides
  // whether to SHOW the Create Account tab, so a full beta is a calm panel
  // rather than a raw error. Failure to read the status must never block
  // sign-up, so the default is "open" and the hook remains the last word.
  const [betaOpen, setBetaOpen] = useState(true)
  useEffect(() => {
    let alive = true
    api.get('/beta/status')
      .then((r) => { if (alive && r && r.open === false) setBetaOpen(false) })
      .catch(() => {})
    return () => { alive = false }
  }, [])
  const betaFullMessage = 'The SOVRANOVA beta is currently full. Thanks so much for your interest — more places open as the beta grows, and existing testers can still sign in.'

  const handleSubmit = async () => {
    if (busy) return
    if (!email || !password) return
    if (mode === 'signup') {
      if (!dob) { setError('Please enter your date of birth.'); return }
      const age = ageFromDob(dob)
      if (age === null) { setError('Please enter a valid date of birth.'); return }
      if (age < 18) { setBlocked(true); return }
      if (!ageConfirm) { setError('Please tick the box to verify your age.'); return }
      if (!terms) { setError('Please accept the Terms & Health Disclaimer to continue.'); return }
      // Same rule the reset screen applies, checked before the round trip.
      const pwProblem = validatePassword(password)
      if (pwProblem) { setError(pwProblem); return }
    }
    setError(null)
    setBusy(true)
    try {
      if (mode === 'signin') { await signIn(email, password) }
      else {
        const result = await signUp(email, password, { date_of_birth: dob, age_confirmed_18: true })
        if (result?.confirmationRequired) setCheckEmail(true)
      }
    } catch (err) {
      const msg = String(err?.message || '')
      if (/rate limit/i.test(msg)) {
        setError("We're sending a lot of emails right now — please wait a few minutes and try again.")
      } else if (/beta is currently full/i.test(msg)) {
        // The registration hook refused: show the designed panel, not the raw text.
        setBetaOpen(false); setMode('signin'); setError(null)
      } else if (/not on the invite list|closed beta/i.test(msg)) {
        // BETA_ALLOWLIST lockdown (emergency lever, normally unset): calm wording
        // instead of the raw server string.
        setError('SOVRANOVA is in a closed beta right now and this account is not on the current invite list. If you were invited, please use the email address the invitation was sent to.')
      } else {
        setError(msg || 'Something went wrong. Please try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  // ── Forgot password ───────────────────────────────────────────────────────
  // The response is identical whether or not the address has an account here:
  // anything else would turn this box into an account-existence oracle.
  const handleResetRequest = async () => {
    if (busy) return
    if (!email) { setError('Please enter your email address.'); return }
    setError(null)
    setBusy(true)
    try {
      await requestPasswordReset(email)
      setNotice(`If an account exists for ${email}, we've sent it a link to set a new password. It may take a minute, and it can land in spam.`)
      setMode('signin')
    } catch (err) {
      setError(/rate limit|too many/i.test(String(err?.message || ''))
        ? "We're sending a lot of emails right now — please wait a few minutes and try again."
        : 'Something went wrong. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const handleResend = async () => {
    if (busy) return
    if (!email) { setError('Enter your email address above and we\'ll send a new link.'); return }
    setError(null)
    setBusy(true)
    try {
      await resendConfirmation(email)
      setLinkError(null)
      setNotice(`If ${email} still needs confirming, a new link is on its way. If it's already confirmed, just sign in below.`)
    } catch (err) {
      setError(/rate limit|too many/i.test(String(err?.message || ''))
        ? "We're sending a lot of emails right now — please wait a few minutes and try again."
        : 'Something went wrong. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  if (checkEmail) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <div className="auth-brand">◎ SOVRANOVA</div>
          <p style={{ fontWeight:600, fontSize:'1.02rem', marginTop:'0.6rem' }}>One more step — check your email</p>
          <p style={{ fontSize:'0.86rem', color:'var(--og-n700, #645c50)', lineHeight:1.6, marginTop:'0.5rem' }}>
            We've sent a confirmation link to <strong>{email}</strong>. Tap the link in that email,
            then come back here and sign in. If you don't see it within a few minutes, check your
            spam or junk folder.
          </p>
          <button className="auth-submit" style={{ marginTop:'1.1rem' }}
            onClick={() => { setCheckEmail(false); setMode('signin'); setError(null) }}>
            I've confirmed — Sign In
          </button>
        </div>
      </div>
    )
  }

  if (blocked) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <div className="auth-brand">◎ SOVRANOVA</div>
          <p style={{ fontWeight:600, fontSize:'1.02rem', marginTop:'0.6rem' }}>Not available for under-18s yet</p>
          <p style={{ fontSize:'0.86rem', color:'var(--og-n700, #645c50)', lineHeight:1.6, marginTop:'0.5rem' }}>
            Thank you for being honest. SOVRANOVA is an 18+ wellbeing app right now — we're still building a safe,
            age-appropriate experience for younger people, so we can't create an account just yet.
          </p>
          <p style={{ fontSize:'0.86rem', color:'var(--og-n700, #645c50)', lineHeight:1.6, marginTop:'0.5rem' }}>
            If you're going through something and want to talk to someone now, the “Urgent help” button at the
            bottom of the screen has support lines for your area, available 24/7.
          </p>
          <button className="auth-submit" style={{ marginTop:'1.1rem' }}
            onClick={() => { setBlocked(false); setMode('signin'); setDob(''); setError(null) }}>
            Back
          </button>
        </div>
      </div>
    )
  }

  // ── Forgot password ───────────────────────────────────────────────────────
  if (mode === 'forgot') {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <div className="auth-brand">◎ SOVRANOVA</div>
          <p className="auth-tagline">We'll send you a link to set a new password.</p>
          <div className="auth-fields">
            <input type="email" placeholder="Email" value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleResetRequest()}
              autoComplete="email" />
          </div>
          {error && <p className="auth-error">{error}</p>}
          <button className="auth-submit" onClick={handleResetRequest} disabled={busy || !email}>
            {busy ? 'Sending…' : 'Send reset link'}
          </button>
          <button type="button" className="auth-linkbtn"
            onClick={() => { setMode('signin'); setError(null) }}>
            Back to sign in
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-brand">◎ SOVRANOVA</div>
        <p className="auth-tagline">Your wellbeing journey begins here.</p>

        {/* A link that no longer works. Said plainly, with the two ways out. */}
        {linkError && (
          <div className="auth-info">
            <p style={{ margin:0, fontWeight:600 }}>That link didn't work</p>
            <p style={{ margin:'0.35rem 0 0' }}>
              {isDeadLinkError(linkError.errorCode)
                ? 'Email links expire, and can only be used once — some email apps also open them automatically before you get there. Nothing is wrong with your account.'
                : 'We couldn\'t complete that step from your email link. Nothing is wrong with your account.'}
            </p>
            <p style={{ margin:'0.35rem 0 0' }}>
              If you were confirming a new account, enter your email below and send yourself a fresh
              link. If you've already confirmed, just sign in.
            </p>
            <button type="button" className="auth-linkbtn" style={{ marginTop:'0.2rem' }}
              onClick={handleResend} disabled={busy}>
              {busy ? 'Sending…' : 'Send me a new confirmation link'}
            </button>
          </div>
        )}
        {notice && <p className="auth-info">{notice}</p>}

        {!betaOpen && <p className="auth-info">{betaFullMessage}</p>}
        <div className="auth-toggle">
          <button className={mode === 'signin' ? 'active' : ''} onClick={() => { setMode('signin'); setError(null) }}>Sign In</button>
          {betaOpen && <button className={mode === 'signup' ? 'active' : ''} onClick={() => { setMode('signup'); setError(null) }}>Create Account</button>}
        </div>
        <div className="auth-fields">
          <input type="email" placeholder="Email" value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            autoComplete="email" />
          <input type="password" placeholder="Password" value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} />
          {mode === 'signup' && (
            <label style={{ display:'flex', flexDirection:'column', gap:'0.25rem', fontSize:'0.78rem', color:'var(--og-n700, #645c50)', fontWeight:600, textAlign:'left' }}>
              Date of birth (you must be 18+)
              <input type="date" value={dob}
                max={new Date().toISOString().split('T')[0]}
                onChange={e => setDob(e.target.value)}
                style={{ colorScheme:'light' }} />
            </label>
          )}
        </div>

        {mode === 'signup' && (
          <label style={tStyles.termsRow}>
            <input type="checkbox" checked={ageConfirm}
              onChange={e => setAgeConfirm(e.target.checked)}
              style={{ marginRight: '0.5rem', cursor: 'pointer', marginTop: '0.15rem' }} />
            <span style={tStyles.termsText}>
              I verify that I am 18 or older and that this is my real date of birth.
            </span>
          </label>
        )}

        {mode === 'signup' && (
          <label style={tStyles.termsRow}>
            <input type="checkbox" checked={terms}
              onChange={e => setTerms(e.target.checked)}
              style={{ marginRight: '0.5rem', cursor: 'pointer', marginTop: '0.15rem' }} />
            <span style={tStyles.termsText}>
              I agree to the{' '}
              <button type="button" style={tStyles.termsLink}
                onClick={() => setShowTerms(true)}>
                Terms of Service &amp; Health Disclaimer
              </button>
            </span>
          </label>
        )}

        {error && <p className="auth-error">{error}</p>}

        {/* Say which acknowledgement is still outstanding, rather than leaving a
            disabled button to be interpreted. Belt and braces with the B1 CSS
            fix: even if a checkbox is ever off-screen again, the reason the
            button won't move is on the screen. */}
        {mode === 'signup' && (!dob || !ageConfirm || !terms) && (
          <p className="auth-notice" style={{ textAlign:'left' }}>
            To continue:{' '}
            {[!dob && 'add your date of birth',
              !ageConfirm && 'tick the 18+ box',
              !terms && 'accept the Terms'].filter(Boolean).join(', ')}.
          </p>
        )}

        <button className="auth-submit" onClick={handleSubmit}
          disabled={loading || busy || !email || !password || (mode === 'signup' && (!terms || !dob || !ageConfirm))}>
          {(loading || busy) ? '...' : mode === 'signin' ? 'Sign In' : 'Create Account'}
        </button>

        {mode === 'signin' && (
          <button type="button" className="auth-linkbtn"
            onClick={() => { setMode('forgot'); setError(null); setNotice(null) }}>
            Forgot password?
          </button>
        )}

        {mode === 'signup' && (
          // Accurate as written: private to the account and never sold, with the
          // processing that actually happens named rather than implied away.
          // The old line ("private, encrypted, and never shared") was not true —
          // session content is processed by our AI providers to generate replies.
          <p className="auth-notice">
            Private to your account and never sold. To reply, SOVRANOVA's AI providers
            process what you share — see the Privacy Policy.
          </p>
        )}
      </div>

      {showTerms && (
        <div style={tStyles.overlay} onClick={() => setShowTerms(false)}>
          <div style={tStyles.modal} onClick={e => e.stopPropagation()}>
            <div style={tStyles.modalHead}>
              <h2 style={tStyles.modalTitle}>Terms of Service &amp; Health Disclaimer</h2>
              <button style={tStyles.closeBtn} onClick={() => setShowTerms(false)}>×</button>
            </div>
            <div style={tStyles.modalBody}>
              <p style={tStyles.sec}>1. Not Medical Advice</p>
              <p style={tStyles.txt}>SOVRANOVA is a wellness and personal development application. It is NOT a medical device, medical service, or mental health treatment. Nothing on this platform constitutes medical advice, diagnosis, or treatment. Always seek the advice of your physician, psychologist, or other qualified health provider with any questions about a medical or mental health condition.</p>

              <p style={tStyles.sec}>2. AI Companion Limitations</p>
              <p style={tStyles.txt}>SOVRANOVA's AI systems support emotional wellbeing and personal growth. They are not licensed therapists, counsellors, psychologists, or medical professionals, and SOVRANOVA does not provide therapy, treatment, or psychological counselling. Nothing in this application should be taken as a claim to be, or to be supervised in real time by, a registered health practitioner. AI responses are not a substitute for professional mental health care.</p>

              <p style={tStyles.sec}>3. Crisis &amp; Emergency</p>
              <p style={tStyles.txt}>If you are in crisis or feel you may harm yourself or others, contact emergency services immediately. Australia: Lifeline 13 11 14 · Emergency 000. United States: 988 Suicide &amp; Crisis Lifeline · Emergency 911. United Kingdom: Samaritans 116 123 · Emergency 999.</p>

              <p style={tStyles.sec}>4. Hypnosis &amp; Audio Sessions</p>
              <p style={tStyles.txt}>Guided hypnosis and audio sessions are relaxation and wellness tools only. Do not use them while driving or operating machinery. Individual results vary. These are not clinical hypnotherapy.</p>

              <p style={tStyles.sec}>5. Privacy &amp; Data</p>
              <p style={tStyles.txt}>Your session data and personal information is stored securely and never sold to third parties. SOVRANOVA uses your data solely to run and personalise your experience. To produce replies, voice and summaries, parts of what you share are processed by the AI providers we use to deliver the service — they are named in our Privacy Policy, and they act on our instructions, not their own. You may request deletion of your data at any time.</p>

              <p style={tStyles.sec}>6. Age Requirement</p>
              <p style={tStyles.txt}>You must be 18 years or older to use SOVRANOVA. By creating an account you confirm you meet this requirement.</p>

              <p style={tStyles.sec}>7. Acceptance</p>
              <p style={tStyles.txt}>By using SOVRANOVA you agree to these terms. These terms may be updated periodically; continued use constitutes acceptance.</p>

              <p style={tStyles.sec}>8. Wellbeing Check-ins</p>
              <p style={tStyles.txt}>With your consent, SOVRANOVA invites you to complete recognised wellbeing questionnaires (such as PHQ-9 and GAD-7) so you can track your own progress over time. These are self-reflection tools, not clinical diagnoses. You may decline or skip them at any time.</p>

              <p style={tStyles.sec}>9. Research &amp; Experimental Features</p>
              <p style={tStyles.txt}>Some features may be clearly labelled as exploratory or part of voluntary research. These are optional, opt-in, and never enabled without your explicit, separate consent. You may withdraw at any time, and withdrawal will not affect your access to the rest of SOVRANOVA.</p>

              <p style={tStyles.sec}>10. How Your Data Is Used</p>
              <p style={tStyles.txt}>Your data is stored securely in our managed database and used solely to run SOVRANOVA, to personalise your experience and, with your consent, to measure your progress. To generate replies, spoken voice and summaries, parts of your content are sent to the AI providers listed in our Privacy Policy — including the text SOVRANOVA speaks aloud, which can reflect what you have just told it. It is never sold. Anthropic and OpenAI do not use it to train their models under the terms that apply to us, and we have turned off the ElevenLabs setting that would allow our data to be used to train theirs. That is a limit on training, not a promise that our providers keep nothing — our Privacy Policy explains what each one does. You may request export or deletion of your data at any time, and you can delete your account and everything in it from Settings.</p>
            </div>
            <button style={tStyles.accept} onClick={() => { setTerms(true); setShowTerms(false) }}>
              I Accept
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const tStyles = {
  termsRow:   { display:'flex', alignItems:'flex-start', fontSize:'0.82rem', margin:'0.25rem 0 0', cursor:'pointer', lineHeight:1.5 },
  termsText:  { color:'var(--og-n700, #645c50)' },
  termsLink:  { background:'transparent', border:'none', color:'var(--og-a700, #8c491a)', fontSize:'0.82rem', cursor:'pointer', textDecoration:'underline', padding:0, fontFamily:'inherit' },
  overlay:    { position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:'1rem' },
  modal:      { background:'#1a0a2e', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'1.25rem', width:'100%', maxWidth:520, maxHeight:'85vh', display:'flex', flexDirection:'column', overflow:'hidden' },
  modalHead:  { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'1.1rem 1.4rem', borderBottom:'1px solid rgba(255,255,255,0.08)' },
  modalTitle: { fontSize:'1.05rem', fontWeight:700, color:'#f5f0e8', margin:0 },
  closeBtn:   { background:'transparent', border:'none', color:'rgba(245,240,232,0.5)', fontSize:'1.4rem', cursor:'pointer', lineHeight:1 },
  modalBody:  { flex:1, overflowY:'auto', padding:'1.1rem 1.4rem', display:'flex', flexDirection:'column', gap:'0.35rem' },
  sec:        { color:'#f5f0e8', marginTop:'0.6rem', fontSize:'0.9rem', fontWeight:600 },
  txt:        { color:'rgba(245,240,232,0.65)', fontSize:'0.84rem', lineHeight:1.65, margin:0 },
  accept:     { margin:'1rem 1.4rem', padding:'0.85rem', background:'linear-gradient(135deg,#5a3a9e,#7b5cf0)', color:'white', border:'none', borderRadius:'0.875rem', fontSize:'0.95rem', fontWeight:700, cursor:'pointer', fontFamily:'inherit' },
}
